import { RoomService } from "../../services/RoomService";
import { useApi } from "./useApi";
import { useConfiguration } from "./useConfiguration";

export const useRoomsApi = () => {
    const { executeApiOperation } = useApi();
    const { appConfig } = useConfiguration();

    const roomService = new RoomService(appConfig.apiBaseUrl);

    const uploadFilesInChunks = async (files, uploadChunkCallback) => {
        const MAX_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
        const MAX_FILE_SIZE = 5 * 1024 * 1024;  // 5 MB

        const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);

        if (validFiles.length === 0) {
            return null;
        }

        const chunks = [];
        let currentChunk = [];
        let currentChunkSize = 0;

        for (const file of validFiles) {
            if (currentChunkSize + file.size > MAX_CHUNK_SIZE && currentChunk.length > 0) {
                chunks.push(currentChunk);
                currentChunk = [file];
                currentChunkSize = file.size;
            } else {
                currentChunk.push(file);
                currentChunkSize += file.size;
            }
        }

        if (currentChunk.length > 0) {
            chunks.push(currentChunk);
        }

        let lastResult = null;
        for (const chunk of chunks) {
            lastResult = await uploadChunkCallback(chunk);
        }

        return lastResult;
    };

    // Room CRUD

    const findAll = async (propertyId) => {
        return executeApiOperation(() => roomService.findAll(propertyId));
    };

    const findById = async (propertyId, roomId) => {
        return executeApiOperation(() => roomService.findById(propertyId, roomId));
    };

    const createRoom = async (propertyId, room) => {
        return executeApiOperation(() => roomService.create(propertyId, room));
    };

    const updateRoom = async (propertyId, room) => {
        return executeApiOperation(() => roomService.update(propertyId, room));
    };

    const deleteRoom = async (propertyId, roomId) => {
        return executeApiOperation(() => roomService.delete(propertyId, roomId));
    };

    // Room pictures — each mutating call returns the updated PicturesAlbum

    const getPictures = async (propertyId, roomId) => {
        return executeApiOperation(() => roomService.getPictures(propertyId, roomId));
    };

    const uploadPictures = async (propertyId, roomId, files) => {
        return uploadFilesInChunks(files, (chunk) =>
            executeApiOperation(() => roomService.uploadPictures(propertyId, roomId, chunk))
        );
    };

    const deletePictures = async (propertyId, roomId, fileNames) => {
        return executeApiOperation(() => roomService.deletePictures(propertyId, roomId, fileNames));
    };

    const reorderPictures = async (propertyId, roomId, fileNamesInOrder) => {
        return executeApiOperation(() => roomService.reorderPictures(propertyId, roomId, fileNamesInOrder));
    };

    return {
        findAll,
        findById,
        createRoom,
        updateRoom,
        deleteRoom,
        getPictures,
        uploadPictures,
        deletePictures,
        reorderPictures,
    };
};
